import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { Vue, Options, prop, setup } from 'vue-class-component';

import { Metadata$FieldType } from '../types/Metadata';
import { required } from '@/helpers/validations.rules';
import { useVuelidate } from '@vuelidate/core';
import { StandardOption } from '@/types/Component';

type ForkFormatters = { [Key in Metadata$FieldType]: RegExp };

const FORMATTERS: ForkFormatters = {
  [Metadata$FieldType.DATE]: /./,
  [Metadata$FieldType.STRING]: /./,
  [Metadata$FieldType.INTEGER]: /^-?\d*$/,
  [Metadata$FieldType.DECIMAL]: /^-?(\d+\.?\d*)?$/,
};

const LENGTHS: { [Key in Metadata$FieldType]: number } = {
  [Metadata$FieldType.DATE]: 100,
  [Metadata$FieldType.STRING]: 1000,
  [Metadata$FieldType.INTEGER]: 9,
  [Metadata$FieldType.DECIMAL]: 15,
};

class Props {
  type = prop<Nullable<Metadata$FieldType>>({ default: null });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  label = prop({ default: '' });
  disabled = prop({ default: false });
  shouldValidateValue?: boolean;
  isDropdown?: boolean;
  dropdownOptions?: StandardOption[];
  forkFormatters?: ForkFormatters;
}

type Emits = {
  onInput: <T>(value: T | string) => void;
};

@Options({
  emits: ['input', 'update:value'],
  validations: (vm: Vue<Props>) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (vm.$props.shouldValidateValue?.value) {
      return { value: { required, $autoDirty: true } };
    }
  },
})
export default class Fork extends Vue.with(Props) {
  declare $props: $Props<Props, Emits, 'value'>;

  private v$ = setup(() => useVuelidate());

  protected get local() {
    return this.value;
  }

  protected set local(local) {
    // remove dot at the end of the value if this is decimal
    if (this.type === Metadata$FieldType.DECIMAL && local) {
      local = local.replace(/\.$/, '');
    }

    this.$emit('input', local);
    this.$emit('update:value', local);
  }

  render() {
    const { type, label, disabled } = this;

    if (this.isDropdown) {
      return (
        <chosen
          errors={this.v$.value?.$errors}
          keyForLabel="label"
          options={this.dropdownOptions}
          placeholder={this.$t('media', 'metadata-field__no-value')}
          vModel={[this.local, 'value']}
        />
      );
    }

    if (!type) {
      return;
    }

    if (type === Metadata$FieldType.DATE) {
      return (
        <datepicker
          disabled={disabled}
          errors={this.v$.value?.$errors}
          label={label}
          vModel={[this.local, 'value']}
        />
      );
    }

    const formatter = this.forkFormatters ? this.forkFormatters[type] : FORMATTERS[type];
    const length = LENGTHS[type];

    return (
      <text-field
        disabled={disabled}
        errors={this.v$.value?.$errors}
        formatter={formatter}
        label={label}
        maxLength={length}
        vModel={[this.local, 'value']}
      />
    );
  }
}
